import { TabList, TabSlot, Tabs, TabTrigger } from 'expo-router/ui';
import { useRef } from 'react';
import { type ScrollView, StyleSheet } from 'react-native';
import { useQuery, useSubscription } from 'urql';
import Nav from '@/components/Custom/Nav';
import * as NavItem from '@/components/Custom/NavItem';
import {
  GetOrganizationDetailLayout,
  OrganizationNavigationFragment,
} from '@/components/Navigation/ContextNavigationFragments';
import { graphql, readFragment } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import useRootStore from '@/stores/useRootStore';
import OrganizationDetailToolbar from './OrganizationDetailToolbar';
import { OrganizationDetailToolbarFragment } from './OrganizationDetailToolbarFragment';

const WatchOrganizationDetailLayout = graphql(/* GraphQL */ `
  subscription WatchOrganizationDetail(
    $organizationId: uuid!
    $personId: String!
  ) {
    event_by_pk(id: $organizationId) {
      ...OrganizationDetailToolbar
    }
  }
`, [OrganizationDetailToolbarFragment]);

export default function OrganizationDetailLayout() {
  const navRef = useRef<ScrollView>(null);

  const organizationId = useRootStore((state) => state.organizationId);

  const personId = useUserId();

  const [{ data }] = useQuery({
    query: GetOrganizationDetailLayout,
    variables: {
      organizationId: organizationId || '',
      personId,
    },
    pause: !organizationId,
  });

  const navigation = readFragment(
    OrganizationNavigationFragment,
    data?.event_by_pk,
  );
  const organization =
    navigation?.id === organizationId ? navigation : undefined;

  useSubscription({
    query: WatchOrganizationDetailLayout,
    variables: {
      organizationId: organizationId || '',
      personId,
    },
    pause: !organization,
  });

  const tabTriggers = [
    {
      name: 'schedules',
      shouldRender: true,
      label: 'Schedules',
    },
    {
      name: 'badges',
      shouldRender: !!organization?.permissions?.edit,
      label: 'Badges',
    },
    {
      name: 'members',
      shouldRender: true,
      label: 'People',
    },
    {
      name: 'teams',
      shouldRender: true,
      label: 'Teams',
    },
  ];

  const visibleTabTriggers = tabTriggers.filter(
    (trigger) => trigger.shouldRender,
  );

  return (
    <>
      <OrganizationDetailToolbar
        fragment={organization ? data?.event_by_pk : undefined}
      />
      <Tabs testID="OrganizationDetailTabs">
        <Nav
          ref={navRef}
          data={visibleTabTriggers}
          keyExtractor={(item) => item.name}
          renderItem={({ item, index }) => (
            <TabTrigger resetOnFocus name={item.name} asChild>
              <NavItem.Container index={index}>
                <NavItem.Label>{item.label}</NavItem.Label>
              </NavItem.Container>
            </TabTrigger>
          )}
        />
        <TabList>
          <TabTrigger
            name="schedules"
            href={{
              pathname: '/organization',
              params: { organizationId },
            }}
          />

          <TabTrigger
            name="badges"
            href={{
              pathname: '/organization/badges',
              params: { organizationId },
            }}
          />
          <TabTrigger
            name="members"
            href={{
              pathname: '/organization/members',
              params: { organizationId },
            }}
          />
          <TabTrigger
            name="teams"
            href={{
              pathname: '/organization/teams',
              params: { organizationId },
            }}
          />
        </TabList>
        <TabSlot key={organizationId} style={style.tabSlot} />
      </Tabs>
    </>
  );
}

const style = StyleSheet.create({
  tabSlot: {
    flex: 1,
  },
});
